import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, Loader2, Globe, Sparkles, BookOpen, GraduationCap } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'हिंदी (Hindi)' },
  { code: 'ta', name: 'தமிழ் (Tamil)' },
  { code: 'te', name: 'తెలుగు (Telugu)' },
  { code: 'bn', name: 'বাংলা (Bengali)' },
  { code: 'mr', name: 'मराठी (Marathi)' },
  { code: 'gu', name: 'ગુજરાતી (Gujarati)' },
  { code: 'kn', name: 'ಕನ್ನಡ (Kannada)' },
  { code: 'ml', name: 'മലയാളം (Malayalam)' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)' },
];

const AryanCareerBuddy = ({ isOpen, onClose, token }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [language, setLanguage] = useState('en');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const messagesEndRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start chat session when opened
  useEffect(() => {
    if (isOpen && !sessionStarted && token) {
      startChatSession();
    }
  }, [isOpen, token]);

  const startChatSession = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/aryan/start?language=${language}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages([{
          role: 'assistant',
          content: data.opening_message,
          timestamp: new Date().toISOString()
        }]);
        setSessionStarted(true);
      }
    } catch (error) {
      console.error('Error starting Aryan session:', error);
      setMessages([{
        role: 'assistant',
        content: "Namaste! 🙏 I'm Aryan, your AI Career Buddy. I'm having a moment - please try again!",
        timestamp: new Date().toISOString()
      }]);
    }
    setIsLoading(false);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/aryan/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: inputMessage,
          language: language
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          timestamp: data.timestamp
        }]);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, I encountered an issue. Please try again! 🙏",
        timestamp: new Date().toISOString()
      }]);
    }
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickActions = [
    { icon: <BookOpen size={16} />, text: "Explore career paths", prompt: "What are the best career paths for someone interested in technology and creativity?" },
    { icon: <GraduationCap size={16} />, text: "University guidance", prompt: "Tell me about the top universities for computer science in India and abroad" },
    { icon: <Sparkles size={16} />, text: "Entrance exams", prompt: "What entrance exams should I prepare for if I want to study engineering?" },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-gradient-to-b from-slate-900 to-slate-800 w-full max-w-2xl h-[80vh] rounded-2xl shadow-2xl border border-amber-500/20 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600/20 to-amber-500/10 p-4 border-b border-amber-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <span className="text-2xl">🧑‍🎓</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-amber-400">Aryan</h2>
                <p className="text-sm text-slate-400">Your AI Career Buddy</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowLanguageSelect(!showLanguageSelect)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-700 transition-colors"
                  data-testid="language-selector"
                >
                  <Globe size={16} />
                  <span className="text-sm">{LANGUAGES.find(l => l.code === language)?.name.split(' ')[0]}</span>
                </button>
                {showLanguageSelect && (
                  <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10 py-1 min-w-[180px]">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setShowLanguageSelect(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-700 transition-colors ${
                          language === lang.code ? 'text-amber-400 bg-slate-700/50' : 'text-slate-300'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
                data-testid="close-aryan"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-amber-600 text-white rounded-br-sm'
                    : 'bg-slate-700/50 text-slate-200 rounded-bl-sm border border-slate-600/50'
                }`}
              >
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {msg.content}
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-slate-700/50 rounded-2xl rounded-bl-sm px-4 py-3 border border-slate-600/50">
                <div className="flex items-center gap-2 text-amber-400">
                  <Loader2 className="animate-spin" size={16} />
                  <span className="text-sm">Aryan is thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions (shown when no messages or few messages) */}
        {messages.length <= 1 && !isLoading && (
          <div className="px-4 pb-2">
            <p className="text-xs text-slate-500 mb-2">Quick actions:</p>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputMessage(action.prompt)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-700/50 text-slate-300 text-xs hover:bg-slate-700 hover:text-amber-400 transition-colors border border-slate-600/50"
                >
                  {action.icon}
                  {action.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-slate-700/50 bg-slate-800/50">
          <div className="flex items-end gap-2">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Aryan anything about careers, universities, exams..."
              className="flex-1 bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 resize-none text-sm"
              rows={1}
              style={{ minHeight: '44px', maxHeight: '120px' }}
              data-testid="aryan-input"
            />
            <button
              onClick={sendMessage}
              disabled={isLoading || !inputMessage.trim()}
              className="p-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="aryan-send"
            >
              <Send size={20} />
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            Aryan can help in {LANGUAGES.length} languages • Powered by LeadEconomics™
          </p>
        </div>
      </div>
    </div>
  );
};

// Floating button to open Aryan
export const AryanFloatingButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg hover:shadow-amber-500/30 hover:scale-105 transition-all flex items-center justify-center group"
      data-testid="aryan-floating-btn"
    >
      <div className="relative">
        <MessageCircle size={28} />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-amber-500 animate-pulse"></span>
      </div>
      <div className="absolute bottom-full mb-2 right-0 bg-slate-900 text-white text-sm px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-amber-500/30">
        Chat with Aryan 🙏
      </div>
    </button>
  );
};

export default AryanCareerBuddy;
